import type { CardDef } from '../../../../core/card';
import { ACID } from '../../../palette';
import { TAU, hashStr, pt, ring, rng, type Pt } from '../geom';
import { box, dot, label, line, solid, type FaceSpec, type Prim } from '../spec';
import { docketFrame, docketInk, faintGrid, halftoneFill } from './frame';

/**
 * **单据 Docket —— 五张技术作业票。**
 *
 * 它们共用同一张版式（`docketFrame`），差别只在中间那张图。
 * 五张图都是**操作**的图解，不是插画：
 *
 * | 牌 | 操作 | 图 |
 * | --- | --- | --- |
 * | Signal 信号 | **分裂** | 一条粗波在中段分成两条，交点一个节点 |
 * | Suspend 挂起 | **半印** | 巨圆只印出上半（网点），下半只剩虚线 |
 * | Await 求值 | **未闭合** | 巨环开着一个口，缺口两端有端子 |
 * | Hash 哈希 | **重排** | 置换图：左列有序、右列打乱、中间交叉 |
 * | Debug 调试 | **递显** | 三个叠放的方块：虚线轮廓 → 网点 → 实心 |
 *
 * ⚠️ **图放进 `spill`，框和字放进 `prims`。** 前者不裁、画在底色之上，
 * 所以它既在牌内可见、又能伸出牌外；后者一律裁到牌内（那是纸上的字）。
 */
export function docketFace(card: CardDef): FaceSpec {
  const ink = docketInk(card);
  return {
    ground: solid(0x000000),
    accent: ink,
    prims: docketFrame(card, ink, []),
    press: true,
    spill: docketArt(card, ink),
  };
}

function docketArt(card: CardDef, ink: number): Prim[] {
  switch (card.local) {
    case 'Signal':
      return signalArt(ink);
    case 'Suspend':
      return suspendArt(ink);
    case 'Await':
      return awaitArt(ink);
    case 'Hash':
      return hashArt(card);
    case 'Debug':
      return debugArt(ink);
    default:
      return [];
  }
}

// ============================================================
// 信号 —— 分裂
// ============================================================

/** 采样一条曲线成折线。`from`/`to` 是 x 范围，`fn` 给 t∈[0,1] → y */
function sample(from: number, to: number, step: number, fn: (t: number) => number): Pt[] {
  const pts: Pt[] = [];
  for (let x = from; x <= to + 1e-9; x += step) {
    pts.push(pt(x, fn((x - from) / (to - from))));
  }
  return pts;
}

function signalArt(ink: number): Prim[] {
  const out: Prim[] = [];
  // 背景：十二条极淡的波，振幅按高斯包络收在中段
  for (let row = 0; row < 12; row++) {
    const y0 = 18 + row * 6.4;
    out.push(
      line(
        sample(-22, 118, 2.4, (t) => {
          const env = Math.exp(-Math.pow((t - 0.5) / 0.42, 2));
          return y0 - Math.sin(t * 7 + row * 0.3) * 3.4 * env;
        }),
        { width: 0.45, color: ink, alpha: 0.28 },
      ),
    );
  }
  // 主波：走到 0.46 处一分为二
  const branch = (t0: number, t1: number, lift: ((t: number) => number) | null): Pt[] => {
    const pts: Pt[] = [];
    for (let x = -22; x <= 118; x += 1.2) {
      const t = (x + 22) / 140;
      if (t < t0 || t > t1) continue;
      const env = Math.exp(-Math.pow((t - 0.4) / 0.32, 2));
      pts.push(pt(x, 52 - Math.sin(t * 9) * 13 * env + (lift === null ? 0 : lift(t))));
    }
    return pts;
  };
  out.push(line(branch(0, 0.47, null), { width: 2.8, color: ink }));
  out.push(line(branch(0.44, 1, (t) => (t - 0.46) * 30), { width: 2.8, color: ink }));
  out.push(
    line(branch(0.44, 1, (t) => -(t - 0.46) * 26), { width: 2.8, color: ink, alpha: 0.68 }),
  );
  // 分叉点
  out.push(dot(-22 + 0.46 * 140, 52, 2.3, solid(ink)));
  // 右侧一道极淡的界
  out.push(line([pt(112, 6), pt(112, 98)], { width: 0.5, color: ink, alpha: 0.45 }));
  return out;
}

// ============================================================
// 挂起 —— 半印
// ============================================================

function suspendArt(ink: number): Prim[] {
  const cx = 50;
  const cy = 58;
  const R = 48;
  const out: Prim[] = [...faintGrid(-10, -6, 110, 57, 5.4, 1.1, ACID.sky, 0.18)];
  // 上半：网点印满了
  out.push(...halftoneFill(ring(cx, cy, R, R, 72, Math.PI, TAU), solid(ink), 4.2, 45, 0.6));
  // 下半：没印出来，只剩虚线
  out.push(
    line(ring(cx, cy, R, R, 72, 0, Math.PI), {
      width: 0.9,
      color: ink,
      alpha: 0.5,
      dash: [2.2, 3.2],
    }),
  );
  // 印到哪儿为止
  out.push(line([pt(-44, cy), pt(144, cy)], { width: 1.6, color: ink }));
  return out;
}

// ============================================================
// 求值 —— 未闭合
// ============================================================

function awaitArt(ink: number): Prim[] {
  const cx = 50;
  const cy = 54;
  const R = 46;
  const gap = (26 * Math.PI) / 180;
  const at = (a: number): Pt => pt(cx + R * Math.cos(a), cy + R * Math.sin(a));
  const out: Prim[] = [
    // 里面一圈参考圆
    { k: 'circle', x: cx, y: cy, r: 27, stroke: { width: 0.6, color: ink, alpha: 0.3 } },
    // 长长的实线：环从 26° 绕到 334°
    line(ring(cx, cy, R, R, 64, gap, TAU - gap), { width: 3.4, color: ink, join: 'round' }),
    // 缺口那一段：本该接上，但没接
    line(ring(cx, cy, R, R, 24, TAU - gap, TAU + gap), {
      width: 1.2,
      color: ink,
      alpha: 0.62,
      dash: [3, 3.6],
    }),
  ];
  // 缺口两端的端子：它们是"接口"，暗示这里本该插上什么
  const p1 = at(gap);
  const p2 = at(TAU - gap);
  for (const p of [p1, p2]) {
    out.push(box(p.x - 4.2, p.y - 4.2, 8.4, 8.4, solid(ink)));
  }
  return out;
}

// ============================================================
// 哈希 —— 重排
// ============================================================

function hashArt(card: CardDef): Prim[] {
  const r = rng(hashStr(card.id));
  const out: Prim[] = [];
  // 背景：一片极淡的条码，像还没读进来的数据
  for (let row = 0; row < 11; row++) {
    for (let col = 0; col < 17; col++) {
      const len = 1.6 + r() * 5.4;
      const x = -18 + col * 7.9;
      const y = 10 + row * 7.2;
      out.push(line([pt(x, y), pt(x + len, y)], { width: 1.8, color: 0xffffff, alpha: 0.17 }));
    }
  }
  // 置换：左列有序、右列打乱，中间连线
  const n = 7;
  const y0 = 24;
  const dy = 10;
  const perm = Array.from({ length: n }, (_, i) => i);
  for (let i = n - 1; i > 0; i--) {
    const j = Math.floor(r() * (i + 1));
    const t = perm[i]!;
    perm[i] = perm[j]!;
    perm[j] = t;
  }
  for (let i = 0; i < n; i++) {
    out.push(
      line([pt(-12, y0 + i * dy), pt(112, y0 + perm[i]! * dy)], {
        width: 0.7,
        color: 0xffffff,
        alpha: 0.8,
      }),
    );
    out.push(label(String(i + 1), 4, y0 + i * dy + 1.2, { size: 3.4, color: 0xffffff, alpha: 0.3 }));
    out.push(
      label(String(perm[i]! + 1), 96, y0 + perm[i]! * dy + 1.2, {
        size: 3.4,
        color: 0xffffff,
        alpha: 0.3,
      }),
    );
  }
  return out;
}

// ============================================================
// 调试 —— 递显
// ============================================================

function debugArt(ink: number): Prim[] {
  const S = 44;
  const positions: readonly Pt[] = [pt(11, 57), pt(28, 40), pt(45, 23)];
  const out: Prim[] = [
    // 一条贯穿的斜线，像"层"的视线
    line([pt(-16, 99), pt(116, 11)], { width: 0.45, color: ink, alpha: 0.28 }),
  ];
  // 最远一层：只有轮廓
  const p0 = positions[0]!;
  out.push({
    k: 'rect',
    x: p0.x,
    y: p0.y,
    w: S,
    h: S,
    stroke: { width: 1, color: ink, alpha: 0.5, dash: [3, 3.2] },
  });
  // 中间一层：网点
  const p1 = positions[1]!;
  out.push(...halftoneFill(ring(p1.x + S / 2, p1.y + S / 2, S / 2, S / 2, 48), solid(ink), 4, 15, 0.5));
  out.push({
    k: 'rect',
    x: p1.x,
    y: p1.y,
    w: S,
    h: S,
    stroke: { width: 0.9, color: ink, alpha: 0.85 },
  });
  // 最近一层：实心。看得见的只有它，但另外两层是它的来路
  const p2 = positions[2]!;
  out.push(box(p2.x, p2.y, S, S, solid(ink)));
  return out;
}

